/**
 * Operation Management - Filters
 *
 * Toggle (All / Today's Tasks) as pill selector
 * + Filter icon + Search + 3 dropdown filters
 * Matching Figma design 573:28047
 */

import { Box, Typography, InputBase, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuSearch } from 'react-icons/lu';

interface IOperationFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  taskTypeFilter?: string;
  onTaskTypeChange?: (value: string | undefined) => void;
  taskStatusFilter?: string;
  onTaskStatusChange?: (value: string | undefined) => void;
  priorityFilter?: string;
  onPriorityChange?: (value: string | undefined) => void;
  todayOnly: boolean;
  onTodayOnlyChange: (value: boolean) => void;
}

const FILTER_SELECT_SX = {
  fontFamily: 'Tajawal, sans-serif',
  fontSize: '14px',
  color: '#435363',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  height: '40px',
  minWidth: '120px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E8EA' },
  '& .MuiSelect-select': { py: '8px', px: '12px' },
};

const OperationFilters = ({
  searchValue,
  onSearchChange,
  taskTypeFilter,
  onTaskTypeChange,
  taskStatusFilter,
  onTaskStatusChange,
  priorityFilter,
  onPriorityChange,
  todayOnly,
  onTodayOnlyChange,
}: IOperationFiltersProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Toggle pill: الكل | مهام اليوم — inside a rounded container */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F6F6F8',
          borderRadius: '100px',
          padding: '4px',
          width: '100%',
          justifyContent: 'center',
        }}
      >
        {/* "الكل" — logically first in RTL */}
        <Box
          onClick={() => onTodayOnlyChange(false)}
          sx={{
            cursor: 'pointer',
            padding: '8px 40px',
            borderRadius: '100px',
            backgroundColor: !todayOnly ? '#FFFFFF' : 'transparent',
            boxShadow: !todayOnly ? '0px 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s',
            width: '50%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              fontWeight: !todayOnly ? 500 : 400,
              color: !todayOnly ? '#435363' : '#8E98A1',
              whiteSpace: 'nowrap',
            }}
          >
            {t('OperationManagement.all')}
          </Typography>
        </Box>
        {/* "مهام اليوم" */}
        <Box
          onClick={() => onTodayOnlyChange(true)}
          sx={{
            cursor: 'pointer',
            padding: '8px 40px',
            borderRadius: '100px',
            backgroundColor: todayOnly ? '#FFFFFF' : 'transparent',
            boxShadow: todayOnly ? '0px 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s',
            width: '50%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              fontWeight: todayOnly ? 500 : 400,
              color: todayOnly ? '#435363' : '#8E98A1',
              whiteSpace: 'nowrap',
            }}
          >
            {t('OperationManagement.todayTasks')}
          </Typography>
        </Box>
      </Box>

      {/* Search + Filters row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {/* Filter icon (mynauiFilter from Figma) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            border: '1px solid #E8E8EA',
            backgroundColor: '#FFFFFF',
            flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7H21M6 12H18M10 17H14" stroke="#8E98A1" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </Box>

        {/* Search */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8EA',
            borderRadius: '8px',
            px: '12px',
            height: '40px',
            minWidth: '200px',
          }}
        >
          <LuSearch size={16} color="#8E98A1" />
          <InputBase
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('OperationManagement.search')}
            sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', flex: 1 }}
          />
        </Box>

        {/* Task Type filter — values must match API automation type names */}
        <Select
          size="small"
          displayEmpty
          value={taskTypeFilter || ''}
          onChange={(e) => onTaskTypeChange?.(e.target.value || undefined)}
          sx={FILTER_SELECT_SX}
        >
          <MenuItem value="">{t('OperationManagement.taskType')}</MenuItem>
          <MenuItem value="CleaningRequest">{t('OperationManagement.cleaning')}</MenuItem>
          <MenuItem value="Maintenance">{t('OperationManagement.maintenance')}</MenuItem>
          <MenuItem value="Inspection">{t('OperationManagement.inspection')}</MenuItem>
          <MenuItem value="Preparation">{t('OperationManagement.preparation')}</MenuItem>
        </Select>

        {/* Task Status filter — values: awaiting_processing, assigned, in_progress, completed, canceled */}
        <Select
          size="small"
          displayEmpty
          value={taskStatusFilter || ''}
          onChange={(e) => onTaskStatusChange?.(e.target.value || undefined)}
          sx={FILTER_SELECT_SX}
        >
          <MenuItem value="">{t('OperationManagement.taskStatusFilter')}</MenuItem>
          <MenuItem value="awaiting_processing">{t('OperationManagement.statusWaiting')}</MenuItem>
          <MenuItem value="assigned">{t('OperationManagement.statusScheduled')}</MenuItem>
          <MenuItem value="in_progress">{t('OperationManagement.statusInProgress')}</MenuItem>
          <MenuItem value="completed">{t('OperationManagement.statusDone')}</MenuItem>
          <MenuItem value="canceled">{t('OperationManagement.statusCancelled')}</MenuItem>
        </Select>

        {/* Priority filter — values: emergency, urgent, routine, low */}
        <Select
          size="small"
          displayEmpty
          value={priorityFilter || ''}
          onChange={(e) => onPriorityChange?.(e.target.value || undefined)}
          sx={FILTER_SELECT_SX}
        >
          <MenuItem value="">{t('OperationManagement.priorityFilter')}</MenuItem>
          <MenuItem value="emergency">{t('OperationManagement.priorityEmergency')}</MenuItem>
          <MenuItem value="urgent">{t('OperationManagement.priorityHigh')}</MenuItem>
          <MenuItem value="routine">{t('OperationManagement.priorityMedium')}</MenuItem>
          <MenuItem value="low">{t('OperationManagement.priorityLow')}</MenuItem>
        </Select>
      </Box>
    </Box>
  );
};

export default OperationFilters;
